<template>
    <section id="contact" class="relative z-10 bg-gradient-to-b from-white via-white/80 to-white backdrop-blur-3xl">
        <div class="grid gap-6 lg:grid-cols-3 lg:gap-24">
            <div class="lg:col-span-2">
                <Banner />
                <form class="mx-auto space-y-4 md:space-y-8" @submit.prevent="onFormSubmit">
                    <div class="grid gap-8 sm:grid-cols-2 sm:gap-4">
                        <FrontendFormField
                            name="name"
                            :label="$t('frontend.contact_form.name')"
                            :required="true"
                            :error-message="form.errors.name">
                            <FrontendTextInput
                                v-model="form.name"
                                name="name"
                                autocomplete="given-name"
                                :required="true"
                                :placeholder="$t('frontend.contact_form.name_placeholder')" />
                        </FrontendFormField>
                        <FrontendFormField
                            name="surname"
                            :label="$t('frontend.contact_form.surname')"
                            :required="true"
                            :error-message="form.errors.surname">
                            <FrontendTextInput
                                v-model="form.surname"
                                name="surname"
                                autocomplete="family-name"
                                :required="true"
                                :placeholder="$t('frontend.contact_form.surname_placeholder')" />
                        </FrontendFormField>
                    </div>
                    <div>
                        <FrontendFormField
                            name="company"
                            :label="$t('frontend.contact_form.company')"
                            :error-message="form.errors.company">
                            <FrontendTextInput
                                v-model="form.company"
                                name="company"
                                autocomplete="organization"
                                :placeholder="$t('frontend.contact_form.company_placeholder')" />
                        </FrontendFormField>
                    </div>
                    <div>
                        <FrontendFormField
                            name="email"
                            :label="$t('frontend.contact_form.email')"
                            :error-message="form.errors.email"
                            :required="true">
                            <FrontendTextInput
                                v-model="form.email"
                                name="email"
                                type="email"
                                autocomplete="email"
                                :required="true"
                                :placeholder="$t('frontend.contact_form.email_placeholder')" />
                        </FrontendFormField>
                    </div>
                    <div>
                        <FrontendFormField
                            name="message"
                            :label="$t('frontend.contact_form.message')"
                            :error-message="form.errors.message"
                            :required="true">
                            <FrontendTextAreaInput
                                v-model="form.message"
                                name="message"
                                :required="true"
                                :placeholder="$t('frontend.contact_form.message_placeholder')" />
                        </FrontendFormField>
                    </div>
                    <div>
                        <FrontendFormField name="privacy" :error-message="form.errors.privacy">
                            <div class="flex flex-row items-center">
                                <Checkbox
                                    v-model:checked="form.privacy"
                                    name="privacy"
                                    :styled="false"
                                    :required="true" />
                                <FrontendInputLabel class="ml-2" name="privacy" :required="true">
                                    <span
                                        class="text-sm"
                                        v-html="
                                            $t('frontend.contact_form.privacy', {
                                                privacy_url: route('policy.show'),
                                                site_url: siteDomain,
                                            })
                                        " />
                                </FrontendInputLabel>
                            </div>
                        </FrontendFormField>
                    </div>
                    <p v-if="form.errors.captcha_token" class="text-error">
                        {{ form.errors.captcha_token }}
                    </p>
                    <button
                        type="submit"
                        :disabled="form.processing"
                        :class="{'opacity-25': form.processing}"
                        class="group ml-auto flex h-12 w-auto items-center overflow-hidden bg-secondary px-5 transition-all duration-300 hover:bg-primary">
                        <span class="relative uppercase tracking-wide text-white group-hover:text-secondary">
                            {{ $t('frontend.contact_form.send_message') }}
                        </span>
                    </button>
                </form>
            </div>
            <div v-if="hasCompanyInfo || hasSocialInfo" class="md:mt-8 border border-black/30 text-black p-8 sm:p-12">
                <!-- Company Info -->
                <template v-if="hasCompanyInfo">
                    <div v-if="legalInfo.legal_address">
                        <h3 class="text-xs font-light uppercase tracking-widest">
                            {{ $t('frontend.contact_form.address') }}
                        </h3>
                        <p class="mt-4">{{ legalInfo.legal_address }}</p>
                        <p class="mt-4">
                            {{ legalInfo.legal_zip }} {{ legalInfo.legal_city }}, {{ legalInfo.legal_country }}
                        </p>
                    </div>
                    <div v-if="legalInfo.legal_phone || legalInfo.legal_email" class="mt-16">
                        <h3 class="text-xs font-light uppercase tracking-widest">
                            {{ $t('frontend.contact_form.contact_info') }}
                        </h3>
                        <ul class="relative z-20 mt-4 space-y-2 font-light">
                            <li v-if="legalInfo.legal_phone">
                                <a :href="`tel:${legalInfo.legal_phone}`" class="flex flex-row items-center">
                                    <PhoneIcon class="w-4 mr-2" />
                                    <span>{{ legalInfo.legal_phone }}</span></a
                                >
                            </li>
                            <li v-if="legalInfo.legal_email">
                                <!-- <a :href="`mailto:${legalInfo.legal_email}`" class="flex flex-row items-center"> -->
                                <div class="flex flex-row items-center">
                                    <EnvelopeIcon class="w-4 mr-2" />
                                    <span>{{ legalInfo.legal_email }}</span>
                                </div>
                                <!-- </a> -->
                            </li>
                        </ul>
                    </div>
                </template>
                <!-- Social Info -->
                <div v-if="hasSocialInfo" class="mt-16">
                    <h3 class="text-xs font-light uppercase tracking-widest">
                        {{ $t('frontend.contact_form.follow_us') }}
                    </h3>
                    <ul class="relative z-20 mt-4 space-y-4 font-light">
                        <li v-if="socialInfo.social_facebook">
                            <a :href="socialInfo.social_facebook" target="_blank" class="flex flex-row items-center"
                                ><FacebookIcon class="w-6 mr-2" alt="Facebook" /><span>Facebook</span></a
                            >
                        </li>
                        <li v-if="socialInfo.social_instagram">
                            <a :href="socialInfo.social_instagram" target="_blank" class="flex flex-row items-center"
                                ><InstagramIcon class="w-6 mr-2" alt="Instagram" /><span>Instagram</span></a
                            >
                        </li>
                        <li v-if="socialInfo.social_linkedin">
                            <a :href="socialInfo.social_linkedin" target="_blank" class="flex flex-row items-center"
                                ><LinkedInIcon class="w-6 mr-2" alt="LinkedIn" /><span>LinkedIn</span></a
                            >
                        </li>
                        <li v-if="socialInfo.social_threads">
                            <a :href="socialInfo.social_threads" target="_blank" class="flex flex-row items-center"
                                ><ThreadsIcon class="w-6 mr-2" alt="Threads" /><span>Threads</span></a
                            >
                        </li>
                        <!-- <li v-if="socialInfo.social_twitter">
                            <a :href="socialInfo.social_twitter" target="_blank" class="flex flex-row items-center"
                                ><TwitterIcon class="w-6 mr-2" alt="X (Twitter)" /><span
                                    >X (Twitter)</span
                                ></a
                            >
                        </li> -->
                        <li v-if="socialInfo.social_youtube">
                            <a :href="socialInfo.social_youtube" target="_blank" class="flex flex-row items-center"
                                ><YouTubeIcon class="w-6 mr-2" alt="YouTube" /><span>YouTube</span></a
                            >
                        </li>
                    </ul>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
import {useReCaptcha} from './use-recaptcha';
import {computed} from 'vue';
import {route as routeFn} from 'ziggy-js';
import {useForm} from '@inertiajs/vue3';
import {Checkbox} from '@/Components/Form';
import {
    FrontendFormField,
    FrontendInputLabel,
    FrontendTextAreaInput,
    FrontendTextInput,
} from '@/Components/Frontend/Form';
import {Banner} from '@/Components/Messaging';
import {FacebookIcon, InstagramIcon, LinkedInIcon, ThreadsIcon, YouTubeIcon} from '@/images/frontend/social_media';
import {EnvelopeIcon, PhoneIcon} from '@heroicons/vue/24/solid';

const props = defineProps({
    siteDomain: {
        type: String,
        required: true,
    },
    legalInfo: {
        type: Object,
        required: true,
        default: () => {},
    },
    socialInfo: {
        type: Object,
        required: true,
        default: () => {},
    },
});

const form = useForm({
    name: null,
    surname: null,
    company: null,
    email: null,
    message: null,
    privacy: false,
    captcha_token: null,
});

const hasCompanyInfo = computed(() => props.legalInfo && Object.keys(props.legalInfo)?.length);
const hasSocialInfo = computed(() => props.socialInfo && Object.keys(props.socialInfo)?.length);

const {executeRecaptcha, recaptchaLoaded} = useReCaptcha();
const onFormSubmit = async () => {
    await recaptchaLoaded();
    form.captcha_token = await executeRecaptcha('contact');
    submit();
};

function submit() {
    form.clearErrors();
    form.post(routeFn('contact.store'), {
        preserveScroll: false,
        preserveState: true,
        onSuccess: () => form.reset(),
    });
}
</script>
